/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { NamespaceModel } from '../models';
import { NamespacesApiEnum } from '../enums/api';
import { Observable, Subject } from 'rxjs';

@Injectable()
export class NamespacesApiService {
    /**
     * Emit when get namespace is called.
     */
    public emitNamespaceResponse: Subject<any> = new Subject<any>();

    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all namespaces for an organisation.
     * @param organisationId
     * @param options
     */
    public getAll(organisationId: string, options?: any): Observable<NamespaceModel[]> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);
            this.api.get(NamespacesApiEnum.GET_NAMESPACES(organisationId), moduleAuth)
                .subscribe(
                    (data: NamespaceModel[]): void => {
                        this.emitNamespaceResponse.next(data);
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Create namespace by organisation id
     * @param organisationId
     * @param namespaceLabel
     * @param options
     */
    public create(organisationId: string, namespaceLabel: any, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(NamespacesApiEnum.CREATE_NAMESPACE(organisationId), namespaceLabel, moduleAuth)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Edit namespace by organisation id
     * @param organisationId
     * @param namespaceId
     * @param namespaceLabel
     * @param options
     */
    public edit(organisationId: string, namespaceId: string, namespaceLabel: any, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.patch(NamespacesApiEnum.PATCH_NAMESPACE(organisationId, namespaceId), namespaceLabel, moduleAuth)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Delete a namespace by organisation id
     * @param organisationId
     * @param namespaceId
     * @param options
     */
    public delete(organisationId: string, namespaceId: string, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(NamespacesApiEnum.DELETE_NAMESPACE(organisationId, namespaceId), moduleAuth)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }
}
